import React from 'react';
import { FiX, FiCopy } from 'react-icons/fi';

const EmbedBlock = ({ title, code, onCopy }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      <button
        type="button"
        onClick={() => onCopy(code)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        <FiCopy className="h-4 w-4" />
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">{code}</pre>
  </div>
);

const AdViewModal = ({ ad, onClose, onCopyEmbed }) => {
  if (!ad) return null;

  const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00';
  const displayMode = ad.displaySettings?.displayMode || 'popup';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
          <h2 className="text-xl font-semibold text-slate-900">Advertisement Details</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <FiX size={22} />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-4">
            {ad.creativeType === 'video' ? (
              <video src={ad.creative?.url} controls className="max-h-72 w-full rounded-lg object-contain" />
            ) : (
              <img src={ad.creative?.url} alt={ad.title || ad.advertiserName} className="max-h-72 w-full rounded-lg object-contain" />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Detail label="Advertiser" value={ad.advertiserName} />
            <Detail label="Title" value={ad.title || '—'} />
            <Detail label="Unique ID" value={ad.uniqueId} mono />
            <Detail label="Status" value={ad.status} />
            <Detail label="Display Mode" value={displayMode} />
            <Detail label="Weight" value={ad.weight} />
            <Detail label="Creative Type" value={ad.creativeType} />
            <Detail label="Impressions" value={ad.impressions || 0} />
            <Detail label="Clicks" value={ad.clicks || 0} />
            <Detail label="CTR" value={`${ctr}%`} />
            <Detail label="Start Date" value={new Date(ad.startDate).toLocaleDateString()} />
            <Detail label="End Date" value={new Date(ad.endDate).toLocaleDateString()} />
            <Detail label="Landing URL" value={ad.landingUrl} className="md:col-span-2" />
            {ad.clickUrl && <Detail label="Click URL" value={ad.clickUrl} className="md:col-span-2" />}
            {ad.description && <Detail label="Description" value={ad.description} className="md:col-span-2" />}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Publisher embed codes</h3>
              <button
                type="button"
                onClick={() => onCopyEmbed(ad)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <FiCopy className="h-4 w-4" />
                Copy primary
              </button>
            </div>

            <EmbedBlock
              title="Popup (recommended — script only)"
              code={ad.embedCodePopup || ad.embedCode}
              onCopy={(code) => onCopyEmbed({ embedCode: code })}
            />

            <EmbedBlock
              title="Inline (requires container div)"
              code={ad.embedCodeInline || ad.embedCode}
              onCopy={(code) => onCopyEmbed({ embedCode: code })}
            />

            <p className="text-xs text-slate-500">
              Popup mode uses Shadow DOM inside the SDK — publishers do not need custom CSS or React components.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value, mono = false, className = '' }) => (
  <div className={className}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`mt-1 text-sm text-slate-800 break-all ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

export default AdViewModal;
